import { useCallback, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { Defaults } from '../../../../../../modules/API/Defaults/defaults'
import type { IReceiptReportLine } from '../../../../../../modules/API/Reciept/models/receipt'
import type { IReceiptReportParams } from '../../../../../../modules/API/Reciept/models/requests'
import type { IClientItem } from '../../../../../../modules/API/Defaults/models/clients'
import type { ISellerItem, ISellerResponse } from '../../../../../../modules/API/Defaults/models/seller'
import { handleNotifications } from '../../../../../../generic/utils/handleNotifications'
import useDataFetcher from '../../../knisot/hooks/useFetchData'
import { Reciept } from '../../../../../../modules/API/Reciept/reciept'
import { calcReceiptsReportStats, filterReceiptLines } from '../helpers/receiptsReport'
import type { IReceiptsReportStats } from '../types/stats'

export interface ReceiptsReportFilterFormProps {
  initialFilters: IReceiptReportParams
  clients: IClientItem[] | null
  sellers: ISellerItem[] | null
  handleSave: (filters: IReceiptReportParams) => void
}

export interface UseReceiptsReportReturn {
  /** null — not loaded yet */
  lines: IReceiptReportLine[] | null
  /** filtered by search */
  rows: IReceiptReportLine[]
  totalCount: number
  hasData: boolean
  isLoading: boolean
  stats: IReceiptsReportStats
  appliedFilters: IReceiptReportParams | null
  searchQuery: string
  handleSearch: (query: string) => void
  isFilterOpen: boolean
  toggleFilter: () => void
  filterFormProps: ReceiptsReportFilterFormProps
}

const LOAD_ERROR_MESSAGE = 'שגיאה בטעינת הדוח'

/** Today by registration date, every client, seller and payment type. */
const buildDefaultFilters = (): IReceiptReportParams => {
  const today = format(new Date(), 'yyyyMMdd')
  return { DateFrom: today, DateTo: today, DateType: 1, CustomerID: 0, SellerID: 0, PaymentType: '' }
}

export const useReceiptsReport = (): UseReceiptsReportReturn => {
  // The same client list receipts are created from; its ClientID goes into the report's CustomerID.
  const { data: clients } = useDataFetcher<IClientItem[]>(Defaults.getClientsGo, '?ClientType=2')
  const { data: sellerResponse } = useDataFetcher<ISellerResponse>(Defaults.getSellerList, 'data')

  // null — not loaded yet.
  const [lines, setLines] = useState<IReceiptReportLine[] | null>(null)
  // The last filters sent — the form reopens with them, also after a failed load.
  const [filters, setFilters] = useState<IReceiptReportParams>(buildDefaultFilters)
  // Filters of the last successful load — the summary shows what the table holds.
  const [appliedFilters, setAppliedFilters] = useState<IReceiptReportParams | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(true)
  const [searchQuery, setSearchQuery] = useState<string>('')

  const toggleFilter = useCallback(() => setIsFilterOpen((prev) => !prev), [])

  const handleSave = useCallback(async (next: IReceiptReportParams) => {
    setFilters(next)
    setIsFilterOpen(false)
    setIsLoading(true)
    try {
      const res = await Reciept.getReceiptsReport(next)
      setLines(res)
      setAppliedFilters(next)
    } catch {
      setIsFilterOpen(true)
      handleNotifications(LOAD_ERROR_MESSAGE, { type: 'error' })
    } finally {
      setIsLoading(false)
    }
  }, [])

  const rows = useMemo(() => filterReceiptLines(lines ?? [], searchQuery), [lines, searchQuery])

  // Stats cover the whole loaded report — the search narrows the table only.
  const stats = useMemo(() => calcReceiptsReportStats(lines ?? [], new Date()), [lines])

  const filterFormProps = useMemo<ReceiptsReportFilterFormProps>(
    () => ({
      initialFilters: filters,
      clients,
      sellers: sellerResponse?.sellers ?? null,
      handleSave,
    }),
    [filters, clients, sellerResponse, handleSave],
  )

  return {
    lines,
    rows,
    totalCount: rows.length,
    hasData: !!lines?.length,
    isLoading,
    stats,
    appliedFilters,
    searchQuery,
    handleSearch: setSearchQuery,
    isFilterOpen,
    toggleFilter,
    filterFormProps,
  }
}
